import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Layers, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errorMessage, useApi } from "@/lib/api";
import type { Group } from "@/lib/types";
import { EmptyState, Loading, PageHeader } from "@/components/app/PageHeader";

export default function GroupsPage() {
  const navigate = useNavigate();
  const { data, loading } = useApi<Group[]>("/groups");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });

  const create = async () => {
    try {
      const { data: g } = await api.post<Group>("/groups", form);
      toast.success("Groupe créé");
      navigate(`/groupes/${g.id}`);
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const btn = <Button data-testid="btn-create-group" onClick={() => { setForm({ name: "", description: "" }); setOpen(true); }} className="bg-blue-600 text-white hover:bg-blue-500"><Plus className="mr-1 h-4 w-4" />Nouveau groupe</Button>;

  return (
    <div>
      <PageHeader eyebrow="Parc" title="Groupes d'écrans" description="Regroupez vos écrans (ex. salle à manger, vitrine) pour leur assigner la même playlist et le même horaire." actions={btn} />
      {loading && !data ? <Loading /> : !data?.length ? (
        <EmptyState testId="groups-empty" icon={<Layers className="h-6 w-6" />} title="Aucun groupe" text="Créez un groupe pour configurer plusieurs écrans d'un coup." action={btn} />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {data.map((g, i) => (
            <Link key={g.id} to={`/groupes/${g.id}`} data-testid={`group-card-${g.id}`} style={{ animationDelay: `${i * 50}ms` }} className="rise panel group p-6 transition-colors hover:border-slate-700">
              <div className="flex items-start justify-between"><Layers className="h-5 w-5 text-cyan-400" /><span className="text-xs text-slate-500">{g.screens_count} écran(s)</span></div>
              <h3 className="mt-4 font-display text-lg font-medium text-slate-100 group-hover:text-blue-300">{g.name}</h3>
              <p className="mt-1 line-clamp-2 text-sm text-slate-400">{g.description || "—"}</p>
              <p className="mt-4 text-xs text-slate-500">{g.config.schedules.length} plage(s) · horloge {g.config.clock ? "activée" : "désactivée"}</p>
            </Link>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-slate-800 bg-slate-900">
          <DialogHeader><DialogTitle>Nouveau groupe</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Nom</Label><Input data-testid="group-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border-slate-700 bg-slate-950/60" /></div>
            <div className="space-y-2"><Label>Description</Label><Textarea data-testid="group-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="border-slate-700 bg-slate-950/60" /></div>
            <Button data-testid="btn-submit-group" disabled={!form.name.trim()} onClick={create} className="w-full bg-blue-600 text-white hover:bg-blue-500">Créer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
